import { Link } from 'react-router-dom';
import {
  LEP_EI_ID,
  EVENT_BUS_CAPABILITIES,
  EVENT_CATALOG_FEATURES,
  ORCHESTRATION_CAPABILITIES,
  MESSAGE_INFRA_FEATURES,
  API_WEBHOOK_FEATURES,
  AI_ORCHESTRATION_CAPABILITIES,
  OBSERVABILITY_SIGNALS,
  PRODUCTION_WIRING_FEATURES,
  INTEGRATION_ANALYTICS,
  SECURITY_CONTROLS,
  FILE_FORMATS,
  SEARCH_CAPABILITIES,
  DISASTER_RECOVERY,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_EVENT_BUS_KPIS,
  DEMO_CATALOG,
  DEMO_ORCHESTRATION,
  DEMO_QUEUES,
  DEMO_API_WEBHOOKS,
  DEMO_AI_ORCHESTRATION,
  DEMO_OBSERVABILITY,
  DEMO_WIRING,
  buildIntegrationDashboard,
} from '../../enterpriseintegration/framework';
import {
  EnterpriseEventBusPanel,
  EventCatalogPanel,
  ServiceOrchestrationPanel,
  MessageInfrastructurePanel,
  APIWebhookPlatformPanel,
  AIOrchestrationPanel,
  IntegrationObservabilityPanel,
  ProductionWiringPanel,
} from '../../components/enterpriseintegration';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseIntegrationPage() {
  const dashboard = buildIntegrationDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_EI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Integration Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enterprise Integration, Event Bus, Service Orchestration & Production Wiring — the nervous system
          of the AI Commerce Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Event Bus Capabilities" value={dashboard.event_bus_capabilities.toString()} />
        <KpiCard label="Catalog Features" value={dashboard.catalog_features.toString()} />
        <KpiCard label="Orchestration Caps" value={dashboard.orchestration_capabilities.toString()} />
        <KpiCard label="Wiring Features" value={dashboard.wiring_features.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Enterprise Event Bus</h2>
        <p className="mt-1 text-sm text-slate-500">{EVENT_BUS_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <EnterpriseEventBusPanel kpis={DEMO_EVENT_BUS_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Event Catalog</h2>
        <p className="mt-1 text-sm text-slate-500">{EVENT_CATALOG_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <EventCatalogPanel entries={DEMO_CATALOG} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Service Orchestration</h2>
          <p className="mt-1 text-sm text-slate-500">{ORCHESTRATION_CAPABILITIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ServiceOrchestrationPanel workflows={DEMO_ORCHESTRATION} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Message Infrastructure</h2>
          <p className="mt-1 text-sm text-slate-500">{MESSAGE_INFRA_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MessageInfrastructurePanel queues={DEMO_QUEUES} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">API & Webhook Platform</h2>
          <p className="mt-1 text-sm text-slate-500">{API_WEBHOOK_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <APIWebhookPlatformPanel endpoints={DEMO_API_WEBHOOKS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Orchestration</h2>
          <p className="mt-1 text-sm text-slate-500">{AI_ORCHESTRATION_CAPABILITIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AIOrchestrationPanel routes={DEMO_AI_ORCHESTRATION} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Observability</h2>
        <p className="mt-1 text-sm text-slate-500">{OBSERVABILITY_SIGNALS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <IntegrationObservabilityPanel streams={DEMO_OBSERVABILITY} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Production Wiring</h2>
        <p className="mt-1 text-sm text-slate-500">{PRODUCTION_WIRING_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductionWiringPanel checks={DEMO_WIRING} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Security</h2>
          <p className="mt-2 text-xs text-slate-500">{SECURITY_CONTROLS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">File Processing</h2>
          <p className="mt-2 text-xs text-slate-500">{FILE_FORMATS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Cache & Search</h2>
          <p className="mt-2 text-xs text-slate-500">{SEARCH_CAPABILITIES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Disaster Recovery</h2>
          <p className="mt-2 text-xs text-slate-500">{DISASTER_RECOVERY.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{INTEGRATION_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
